import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';

import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../book-create/login-validation.component/login-validation.component';

import { AlertService, AlertType } from '../../shared/services/alert.service';
import { AuthorSettingsService } from '../../shared/services/apis/platform/author-settings.service';
import { ReturnStruct } from '../../shared/services/structs/return.struct';
import { AuthorSettingsSaveRequest } from '../../shared/services/requests/platform/author-settings-save.request';
import { CouponStruct } from '../../shared/services/structs/author-settings.struct';
import { AuthorSettingsGetResponse } from '../../shared/services/responses/platform/author-settings-get.response';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { CouponApplyModalComponent } from './coupon-apply-modal.component/coupon-apply-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { PasswordChangeModalComponent } from './password-change-modal/password-change-modal.component';

type NullableDate = Date | null;

interface CouponVM {
    idCoupon?: number;
    couponCode: string;
    isActive: boolean;
    validFrom: Date | null;
    validTo: Date | null;
    discountPercent?: number;
    discountValue?: number;

    _lockedCode?: string;
    _errors?: string[];

    _discountMode: 'percent' | 'value'; // <-- novo
}

@Component({
    selector: 'app-author-settings',
    standalone: true,
    templateUrl: 'author-settings.component.html',
    styleUrl: 'author-settings.component.css',
    imports: [CommonModule,
        LoginValidationComponent,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MenuComponent,
        MatOptionModule,
        MatRadioModule,
        FooterComponent,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatSelectModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthorSettingsComponent implements OnInit {
    isLoading = false;

    // leitura (somente exibição)
    userEmail = '';
    userCpf?: string;

    // form (editáveis)
    form!: FormGroup;

    // lista de cupons
    coupons: CouponVM[] = [];

    constructor(
        private service: AuthorSettingsService,
        private alert: AlertService,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.form = this.fb.group({
            userName: ['', [Validators.required, Validators.minLength(2)]],
            mobilePhone: ['', [this.brPhoneValidator]]
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onLoginValidated(logged: boolean) {
        if (!logged) return;
        this.fetch();
    }

    // ===== Fetch =====
    fetch(): void {
        this.isLoading = true;
        this.service.Get().subscribe({
            next: (res: AuthorSettingsGetResponse) => {
                if (res.isError) {
                    this.fail(res.errorDescription || 'Erro ao carregar dados.');
                    return;
                }

                this.userEmail = res.userEmail;
                this.userCpf = res.userCpf || undefined;

                this.form.patchValue({
                    userName: res.userName,
                    mobilePhone: res.mobilePhone || ''
                });

                this.coupons = (res.listCoupon || []).map(c => {
                    const mode: 'percent' | 'value' =
                        c.discountValue != null && Number(c.discountValue) > 0 ? 'value' : 'percent';

                    return {
                        idCoupon: c.idCoupon ?? undefined,
                        couponCode: (c.couponCode || '').toUpperCase(),
                        isActive: !!c.isActive,
                        validFrom: c.validFrom ? new Date(c.validFrom) : null,
                        validTo: c.validTo ? new Date(c.validTo) : null,
                        discountPercent: c.discountPercent ?? undefined,
                        discountValue: c.discountValue ?? undefined,
                        _lockedCode: (c.couponCode || '').toUpperCase(),
                        _errors: [],
                        _discountMode: mode
                    } as CouponVM;
                });

                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: () => this.fail('Erro inesperado ao carregar dados.')
        });
    }

    // ===== Save =====
    save(): void {
        // valida form
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alert.show('Erro', 'Preencha os dados do usuário corretamente.', AlertType.error);
            return;
        }

        // valida cupons
        if (!this.validateCoupons()) {
            this.alert.show('Erro', 'Corrija os erros nos cupons antes de salvar.', AlertType.error);
            return;
        }

        const req: AuthorSettingsSaveRequest = {
            idUser: 0,
            userName: String(this.form.value.userName || '').trim(),
            mobilePhone: this.normalizePhone(this.form.value.mobilePhone || ''),
            listCoupon: this.coupons.map(vm => ({
                idCoupon: vm.idCoupon,
                couponCode: this.getEffectiveCode(vm),
                isActive: vm.isActive,
                validFrom: this.toIsoTodayIfNull(vm.validFrom),
                validTo: this.toIsoOrNow(vm.validTo),
                discountPercent: vm._discountMode === 'percent' ? this.cleanPercent(vm.discountPercent) : undefined,
                discountValue: vm._discountMode === 'value' ? this.cleanMoney(vm.discountValue) : undefined
            }))
        };

        this.isLoading = true;
        this.service.Save(req).subscribe({
            next: (res: ReturnStruct) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alert.show('Erro', res.errorDescription || 'Erro ao salvar.', AlertType.error);
                    this.cdr.markForCheck();
                    return;
                }
                this.alert.show('Sucesso', 'Alterações salvas com sucesso!', AlertType.success);
                // Recarrega para refletir locks/normalizações vindas do backend
                this.fetch();
            },
            error: () => this.fail('Erro inesperado ao salvar.')
        });
    }

    // ===== Coupon helpers =====
    addCoupon(): void {
        const today = new Date();
        const plus30 = new Date(today); plus30.setDate(plus30.getDate() + 30);

        this.coupons.unshift({
            couponCode: '',
            isActive: true,
            validFrom: today,
            validTo: plus30,
            discountPercent: undefined,
            discountValue: undefined,
            _lockedCode: undefined,
            _errors: [],
            _discountMode: 'percent' // <-- padrão
        });
        this.cdr.markForCheck();
    }

    removeNewCoupon(index: number): void {
        const c = this.coupons[index];
        if (c.idCoupon) {
            // não removemos cupons existentes; desative no lugar
            this.alert.show('Aviso', 'Cupons existentes não são removidos aqui. Desative o cupom.', AlertType.warning);
            return;
        }
        this.coupons.splice(index, 1);
        this.cdr.markForCheck();
    }

    toggleDiscountMode(index: number): void {
        const vm = this.coupons[index];
        vm._discountMode = vm._discountMode === 'percent' ? 'value' : 'percent';
        if (vm._discountMode === 'percent') {
            vm.discountValue = undefined;
        } else {
            vm.discountPercent = undefined;
        }
        this.cdr.markForCheck();
    }

    onPercentInput(i: number): void {
        const vm = this.coupons[i];
        vm.discountPercent = this.cleanPercent(vm.discountPercent);
    }

    onValueInput(i: number): void {
        const vm = this.coupons[i];
        vm.discountValue = this.cleanMoney(vm.discountValue);
    }

    private cleanMoney(v?: number): number | undefined {
        if (v == null) return undefined;
        const n = Number(v);
        if (Number.isNaN(n)) return undefined;
        return Math.max(0, Math.round(n * 100) / 100);
    }

    private toIsoTodayIfNull(d: Date | null): string {
        const date = d instanceof Date ? d : new Date();
        return date.toISOString();
    }

    toggleCouponActive(index: number): void {
        this.coupons[index].isActive = !this.coupons[index].isActive;
        this.cdr.markForCheck();
    }

    onCouponCodeInput(index: number): void {
        const vm = this.coupons[index];
        vm.couponCode = (vm.couponCode || '').trim().toUpperCase();
        this.cdr.markForCheck();
    }

    onDateChange(): void {
        // apenas marca para validação em tempo real
        this.cdr.markForCheck();
    }

    // ===== Validation =====
    private validateCoupons(): boolean {
        let ok = true;
        const seen = new Map<string, number>();

        this.coupons.forEach(c => {
            const code = (this.getEffectiveCode(c) || '').toUpperCase();
            if (code) seen.set(code, (seen.get(code) || 0) + 1);
        });

        this.coupons.forEach(c => {
            const errors: string[] = [];
            const code = (this.getEffectiveCode(c) || '').toUpperCase();
            if (!code) errors.push('Código obrigatório.');
            if (code && (seen.get(code) || 0) > 1) errors.push('Código duplicado na lista.');

            if (c._discountMode === 'percent') {
                const pct = this.cleanPercent(c.discountPercent);
                if (pct == null) errors.push('Percentual obrigatório.');
                else if (!(pct > 0 && pct <= 100)) errors.push('Percentual deve ser > 0 e ≤ 100.');
            } else {
                const val = this.cleanMoney(c.discountValue);
                if (val == null || val <= 0) errors.push('Valor de desconto deve ser maior que zero.');
                // Limite de custo de produção é aplicado no backend/checkout.
            }

            const start = c.validFrom ?? new Date();
            if (!c.validTo) errors.push('Data final obrigatória.');
            if (c.validTo && start > c.validTo) errors.push('Período inválido: início deve ser ≤ fim.');

            c._errors = errors;
            if (errors.length) ok = false;
        });

        return ok;
    }

    // ===== Utils =====
    private getEffectiveCode(vm: CouponVM): string {
        // Se já existe no backend, o código é travado (usa o locked original)
        if (vm.idCoupon && vm._lockedCode) return vm._lockedCode;
        return (vm.couponCode || '').trim().toUpperCase();
    }

    private cleanPercent(v?: number): number | undefined {
        if (v == null) return undefined;
        const n = Number(v);
        if (Number.isNaN(n)) return undefined;
        return Math.round(n * 100) / 100;
    }

    private toIsoOrNow(d: NullableDate): string {
        const date = d instanceof Date ? d : new Date();
        return date.toISOString();
    }

    private fail(msg: string): void {
        this.isLoading = false;
        this.alert.show('Erro', msg, AlertType.error);
        this.cdr.markForCheck();
    }

    // ===== Phone (BR) =====
    private normalizePhone(raw: string): string {
        if (!raw) return '';
        let s = String(raw).trim();
        s = s.replace(/[^\d+]/g, '');
        // remove +55 duplicado
        if (s.startsWith('+55')) s = s.replace('+55', '');
        // mantém apenas dígitos
        s = s.replace(/\D/g, '');
        return s;
    }

    private brPhoneValidator(control: AbstractControl): ValidationErrors | null {
        const v = String(control.value || '').trim();
        if (!v) return null; // opcional

        // aceita formatos com/sem +55, com DDD válido e 10-11 dígitos (fixo/cel)
        let s = v.replace(/[^\d+]/g, '');
        if (s.startsWith('+55')) s = s.replace('+55', '');
        s = s.replace(/\D/g, '');

        if (s.length !== 10 && s.length !== 11) return { brPhone: true };

        const ddd = Number(s.substring(0, 2));
        if (ddd < 11 || ddd > 99) return { brPhone: true };

        return null;
    }

    // ===== Senha (modal) =====
    openChangePasswordModal(): void {
        const ref = this.dialog.open(PasswordChangeModalComponent, {
            width: '520px',
            disableClose: true
        });

        ref.afterClosed().subscribe((changed: boolean) => {
            if (changed) {
                this.alert.show('Sucesso', 'Senha alterada com sucesso.', AlertType.success);
                // Se precisar, faça algo após a troca (ex.: logout/refresh).
            }
        });
    }

    openApplyModal(index: number): void {
        const c = this.coupons[index];
        if (!c.idCoupon) {
            this.alert.show('Aviso', 'Salve o cupom antes de configurar a aplicação.', AlertType.warning);
            return;
        }

        const ref = this.dialog.open(CouponApplyModalComponent, {
            width: '720px',
            data: { idCoupon: c.idCoupon }
        });

        ref.afterClosed().subscribe((saved: boolean) => {
            if (saved) {
                // se quiser, recarregue algo ou apenas confirme
                this.alert.show('Sucesso', 'Aplicação atualizada.', AlertType.success);
            }
        });
    }
}
